import { useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { api } from '../lib/api';
import { fetchSession } from '../store/slices/authSlice';

// Required scopes for corporation sync
const REQUIRED_CORP_SCOPES = [
  'esi-assets.read_corporation_assets.v1',
  'esi-corporations.read_blueprints.v1',
  'esi-markets.read_corporation_orders.v1',
  'esi-industry.read_corporation_jobs.v1',
];

function hasAllCorporationScopes(scopes: string | null): boolean {
  if (!scopes) return false;
  const scopeArray = scopes.split(' ');
  return REQUIRED_CORP_SCOPES.every((s) => scopeArray.includes(s));
}

function Settings() {
  const dispatch = useAppDispatch();
  const { ppsAccount, sessionToken } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [togglingCorpSync, setTogglingCorpSync] = useState<string | null>(null);
  const [syncingCorp, setSyncingCorp] = useState<string | null>(null);

  const handleAddCharacter = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await api.post<{ url: string }>('/auth/add-character');
      window.location.href = response.data.url;
    } catch (err) {
      setError('Failed to initiate character addition');
      setLoading(false);
    }
  };

  const handleRemoveCharacter = async (characterId: string) => {
    if (characterId === ppsAccount?.mainCharacterId) {
      setError('Cannot remove main character');
      return;
    }

    if (!confirm('Are you sure you want to remove this character?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await api.delete(`/auth/remove-character/${characterId}`);
      setSuccess('Character removed successfully. Please refresh the page.');
    } catch (err) {
      setError('Failed to remove character');
    } finally {
      setLoading(false);
    }
  };

  const handleReauthCharacter = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Uses the same endpoint as add-character - when re-authenticating an existing character,
      // the callback will just update the tokens
      const response = await api.post<{ url: string }>('/auth/add-character');
      window.location.href = response.data.url;
    } catch (err) {
      setError('Failed to initiate re-authentication');
      setLoading(false);
    }
  };

  const handleToggleCorpSync = useCallback(async (characterId: string, currentValue: boolean, corporationName: string | null) => {
    // If disabling, show confirmation
    if (currentValue) {
      const confirmed = confirm(
        `Disabling corporation sync for this character.\n\n` +
        `If this is the only character syncing "${corporationName || 'this corporation'}", all corporation data (assets, blueprints, industry jobs, buy orders) will be deleted.\n\n` +
        `Continue?`
      );
      if (!confirmed) return;
    }

    setTogglingCorpSync(characterId);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post<{
        success: boolean;
        includeCorporation: boolean;
        corporationName: string | null;
        dataDeleted: {
          assetsDeleted: number;
          blueprintsDeleted: number;
          jobsDeleted: number;
          ordersDeleted: number;
        } | null;
      }>(`/settings/character/${characterId}/corporation-sync`, {
        enabled: !currentValue,
      });

      // Refresh session to get updated includeCorporation value
      if (sessionToken) {
        dispatch(fetchSession(sessionToken));
      }

      // Show notification about data deletion if applicable
      if (response.data.dataDeleted) {
        const { assetsDeleted, blueprintsDeleted, jobsDeleted, ordersDeleted } = response.data.dataDeleted;
        const totalDeleted = assetsDeleted + blueprintsDeleted + jobsDeleted + ordersDeleted;
        if (totalDeleted > 0) {
          setSuccess(
            `Corporation sync disabled. Deleted ${assetsDeleted} assets, ${blueprintsDeleted} blueprints, ${jobsDeleted} jobs, ${ordersDeleted} orders for "${response.data.corporationName}".`
          );
        } else {
          setSuccess('Corporation sync disabled.');
        }
      } else if (!currentValue) {
        setSuccess('Corporation sync enabled.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to toggle corporation sync');
    } finally {
      setTogglingCorpSync(null);
    }
  }, [dispatch, sessionToken]);

  const handleSyncCorporation = useCallback(async (characterId: string) => {
    setSyncingCorp(characterId);
    setError(null);
    setSuccess(null);
    try {
      const response = await api.post<{
        success: boolean;
        corporationChanged: boolean;
        allianceChanged: boolean;
        corporationName: string | null;
        previousCorporationName: string | null;
        oldCorpDataDeleted: {
          assetsDeleted: number;
          blueprintsDeleted: number;
          jobsDeleted: number;
          ordersDeleted: number;
        } | null;
        corpSyncDisabled: boolean;
      }>(`/settings/character/${characterId}/sync-corporation`);

      // Refresh session to get updated corporation info
      if (sessionToken) {
        dispatch(fetchSession(sessionToken));
      }

      if (response.data.corporationChanged) {
        let message = `Corporation updated: ${response.data.previousCorporationName || 'None'} → ${response.data.corporationName || 'None'}`;

        // If corp sync was disabled due to corp change
        if (response.data.corpSyncDisabled) {
          message += '. Corporation sync has been disabled (re-enable for new corporation).';
        }

        // If old corp data was deleted
        if (response.data.oldCorpDataDeleted) {
          const { assetsDeleted, blueprintsDeleted, jobsDeleted, ordersDeleted } = response.data.oldCorpDataDeleted;
          const totalDeleted = assetsDeleted + blueprintsDeleted + jobsDeleted + ordersDeleted;
          if (totalDeleted > 0) {
            message += ` Deleted ${assetsDeleted} assets, ${blueprintsDeleted} blueprints, ${jobsDeleted} jobs, ${ordersDeleted} orders from old corporation.`;
          }
        }

        setSuccess(message);
      } else {
        setSuccess('Corporation info is up to date');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to sync corporation info');
    } finally {
      setSyncingCorp(null);
    }
  }, [dispatch, sessionToken]);

  return (
    <Layout>
      <div className="container mx-auto px-8 py-8 h-full overflow-auto">
        <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <button
            onClick={handleAddCharacter}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            + Add Character
          </button>
        </div>

        {error && (
          <div className="bg-red-500 bg-opacity-10 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-500 bg-opacity-10 border border-green-500 text-green-400 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        <div className="bg-gray-800 rounded-xl shadow-2xl border border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700">
            <h2 className="text-xl font-semibold text-white">
              Registered Characters
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Character
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Corporation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Alliance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Corp Sync
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {ppsAccount?.eveAccounts && ppsAccount.eveAccounts.length > 0 ? (
                  ppsAccount.eveAccounts.map((character) => (
                    <tr key={character.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <img
                            src={`https://images.evetech.net/characters/${character.characterId}/portrait?size=64`}
                            alt={character.characterName}
                            className="w-10 h-10 rounded-lg"
                          />
                          <div>
                            <div className="text-sm font-medium text-white">
                              {character.characterName}
                            </div>
                            <div className="text-xs text-gray-400">
                              ID: {character.characterId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {character.corporationId && character.corporationName ? (
                          <div className="flex items-center space-x-2">
                            <img
                              src={`https://images.evetech.net/corporations/${character.corporationId}/logo?size=32`}
                              alt={character.corporationName}
                              className="w-6 h-6 rounded"
                            />
                            <span className="text-sm text-gray-300">
                              {character.corporationName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {character.allianceId && character.allianceName ? (
                          <div className="flex items-center space-x-2">
                            <img
                              src={`https://images.evetech.net/alliances/${character.allianceId}/logo?size=32`}
                              alt={character.allianceName}
                              className="w-6 h-6 rounded"
                            />
                            <span className="text-sm text-gray-300">
                              {character.allianceName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {character.characterId === ppsAccount.mainCharacterId ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-600 text-white">
                            Main Character
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-600 text-gray-200">
                            Alt Character
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {hasAllCorporationScopes(character.scopes) ? (
                          <button
                            onClick={() => handleToggleCorpSync(character.characterId, character.includeCorporation, character.corporationName)}
                            disabled={togglingCorpSync === character.characterId}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                              character.includeCorporation ? 'bg-blue-600' : 'bg-gray-600'
                            } ${togglingCorpSync === character.characterId ? 'opacity-50' : ''}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                character.includeCorporation ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        ) : (
                          <span
                            className="text-xs text-gray-500 cursor-help"
                            title="Missing required corporation scopes. Re-authenticate with corporation scopes to enable."
                          >
                            N/A
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleSyncCorporation(character.characterId)}
                            disabled={loading || syncingCorp === character.characterId}
                            className="text-green-400 hover:text-green-300 disabled:opacity-50"
                            title="Sync corporation info from ESI"
                          >
                            {syncingCorp === character.characterId ? 'Syncing...' : 'Sync Corp'}
                          </button>
                          <button
                            onClick={handleReauthCharacter}
                            disabled={loading}
                            className="text-blue-400 hover:text-blue-300 disabled:opacity-50"
                            title="Re-authenticate to refresh ESI tokens"
                          >
                            Re-auth
                          </button>
                          {character.characterId !== ppsAccount.mainCharacterId && (
                            <button
                              onClick={() => handleRemoveCharacter(character.characterId)}
                              disabled={loading}
                              className="text-red-400 hover:text-red-300 disabled:opacity-50"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-400">
                      No characters found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  );
}

export default Settings;
